import { Component, OnInit, ChangeDetectionStrategy, inject } from '@angular/core';
import { FormBuilder, FormGroup, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialogTitle, MatDialogContent, MatDialogActions } from '@angular/material/dialog';
import { ProductTaskBoard } from 'src/app/models/product_task_board.model';
import { ProductTaskBoardTimeBudget } from 'src/app/models/product_task_board_time_budget.model';
import { CompanyProductTeam } from 'src/app/models/company_product_team.model';
import { User } from 'src/app/models/user.model';
import { WorkingTimeCategory } from 'src/app/models/working_time_category.model';
import { ApiService } from 'src/app/services/api.service';
import { UserService } from 'src/app/services/user.service';
import { CdkScrollable } from '@angular/cdk/scrolling';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatSelect } from '@angular/material/select';
import { MatOption } from '@angular/material/autocomplete';
import { MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { map } from 'rxjs';

@Component({
  selector: 'app-task-from-todo-to-progress-dialog',
  templateUrl: './task-from-todo-to-progress-dialog.component.html',
  styleUrl: './task-from-todo-to-progress-dialog.component.css',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    MatDialogTitle,
    CdkScrollable,
    MatDialogContent,
    FormsModule,
    ReactiveFormsModule,
    MatFormField,
    MatLabel,
    MatSelect,
    MatOption,
    MatDialogActions,
    MatButton,
    MatIcon,
  ],
})
export class TaskFromTodoToProgressDialogComponent implements OnInit {
  dialogRef = inject<MatDialogRef<TaskFromTodoToProgressDialogComponent>>(MatDialogRef);
  data = inject<{
    product_task_board_id: number;
    company_product_id: number;
  }>(MAT_DIALOG_DATA);
  private api = inject(ApiService);
  protected fb = inject(FormBuilder);
  private user = inject(UserService);

  product_task_board: ProductTaskBoard | undefined;
  users: User[] | undefined;
  form: FormGroup;

  constructor() {
    this.form = this.fb.group({
      active_product_task_board_time_budget_id: ['', Validators.required],
      assignee_id: ['', Validators.required],
    });
  }

  ngOnInit(): void {
    this.fetchTask();
    this.fetchProductionTeam();
    this.listenActiveCompanyProductIdChanges();
  }

  async submit() {
    await this.api
      .updateProductTaskBoardTimeBudget(this.form.value.active_product_task_board_time_budget_id, {
        assignee_id: this.form.value.assignee_id,
      })
      .toPromise();
    this.product_task_board = await this.api
      .updateProductTaskBoard(this.product_task_board!.id, {
        active_product_task_board_time_budget_id: this.form.value.active_product_task_board_time_budget_id,
      })
      .pipe(
        map(data => {
          return {
            ...data,
            ro_time_registered: this.product_task_board!.ro_time_registered ?? undefined,
            active_product_task_board_time_budget: data.active_product_task_board_time_budget
              ? {
                  ...data.active_product_task_board_time_budget,
                  ro_time_registered: this.product_task_board!.active_product_task_board_time_budget?.ro_time_registered ?? undefined,
                }
              : this.product_task_board!.active_product_task_board_time_budget,
            product_task_board_time_budgets: data.product_task_board_time_budgets.map((tb: ProductTaskBoardTimeBudget) => {
              return {
                ...tb,
                ro_time_registered:
                  this.product_task_board!.product_task_board_time_budgets.find(curr_tb => curr_tb.id === tb.id)?.ro_time_registered ??
                  undefined,
              };
            }),
          };
        })
      )
      .toPromise();
    this.dialogRef.close(this.product_task_board);
  }

  isTimeBudgetSelectable(time_budget: ProductTaskBoardTimeBudget): boolean {
    return (
      time_budget.completed_by_user_id == null &&
      ((time_budget.budgetable as WorkingTimeCategory).slug == null ||
        (time_budget.budgetable as WorkingTimeCategory).slug !== 'review-task')
    );
  }

  private fetchTask() {
    this.api
      .getProductTaskBoard(this.data.product_task_board_id, {
        with: 'product_task_board_time_budgets..budgetable,active_product_task_board_time_budget..budgetable',
        append: 'ro_time_registered',
      })
      .subscribe(data => {
        this.product_task_board = data;

        this.form.patchValue({
          active_product_task_board_time_budget_id: this.product_task_board.active_product_task_board_time_budget_id,
          assignee_id: this.product_task_board.active_product_task_board_time_budget?.assignee_id ?? this.user.getCurrentUser().id,
        });

        this.checkIfIsNecessary();
      });
  }

  private fetchProductionTeam() {
    this.api.getCompanyProductActiveProductionTeam(this.data.company_product_id.toString()).subscribe(data => {
      this.users = data.bisual_users.map((obj: any) => obj.user);
      data.company_users.forEach(cu => this.users!.push(cu));
    });
  }

  private listenActiveCompanyProductIdChanges() {
    this.form.controls['active_product_task_board_time_budget_id'].valueChanges.subscribe((data: number) => {
      if (data != null) {
        const time_budget: ProductTaskBoardTimeBudget = this.product_task_board!.product_task_board_time_budgets.find(
          tb => tb.id == data
        ) as ProductTaskBoardTimeBudget;
        this.form.controls['assignee_id'].patchValue(
          time_budget.assignee_id != null ? time_budget.assignee_id : this.form.value['assignee_id']
        );
      }
    });
  }

  private checkIfIsNecessary() {
    let budget_to_assign: any = null;
    let count_budgets_assignable: number = 0;

    for (const time_budget of this.product_task_board!.product_task_board_time_budgets) {
      if (this.isTimeBudgetSelectable(time_budget) && time_budget.assignee_id != null) {
        count_budgets_assignable++;
        budget_to_assign = time_budget;
      }
    }

    if (count_budgets_assignable === 1 && budget_to_assign != null) {
      this.form.patchValue({
        active_product_task_board_time_budget_id: budget_to_assign.id,
        assignee_id: budget_to_assign.assignee_id,
      });

      this.submit();
    }
  }
}
